import { Core, NodeCollection } from "cytoscape";
import { atom } from "nanostores";

import { NodeGroup } from "../types/copilot.ts";
import { $cytoscapeInstance } from "./graph/ui.ts";

// Padding for cytoscape fit operations (in pixels)
const FIT_PADDING = 50;
// Fallback delay for fit operations in case render event doesn't fire (in milliseconds)
const FIT_FALLBACK_DELAY = 100;
// Delay for triggering selection events to ensure highlighting is applied (in milliseconds)
const SELECTION_TRIGGER_DELAY = 50;

export const $aiContextGroups = atom<NodeGroup[]>([]);

// Helper to generate a unique group ID
const generateGroupId = (): string => {
  return `group_${crypto.randomUUID()}`;
};

// Helper to generate a group name
const generateGroupName = (groupIndex: number): string => {
  return `Attachment ${groupIndex + 1}`;
};

export const addNodesToAIContext = (nodeIds: string[]): void => {
  if (nodeIds.length === 0) return;

  const currentGroups = $aiContextGroups.get();
  const groupId = generateGroupId();
  const groupName = generateGroupName(currentGroups.length);

  const newGroup: NodeGroup = {
    createdAt: new Date().toISOString(),
    id: groupId,
    name: groupName,
    nodeIds: [...new Set(nodeIds)], // Remove duplicates
  };

  $aiContextGroups.set([...currentGroups, newGroup]);
};

export const removeGroupFromAIContext = (groupId: string): void => {
  const currentGroups = $aiContextGroups.get();
  $aiContextGroups.set(currentGroups.filter((group) => group.id !== groupId));
};

export const updateGroupName = (groupId: string, newName: string): void => {
  const currentGroups = $aiContextGroups.get();
  $aiContextGroups.set(
    currentGroups.map((group) =>
      group.id === groupId ? { ...group, name: newName } : group,
    ),
  );
};

export const clearAIContext = (): void => {
  $aiContextGroups.set([]);
};

// Get all node IDs from all groups (for backward compatibility)
export const getAllNodeIds = (): string[] => {
  const groups = $aiContextGroups.get();
  return groups.flatMap((group) => group.nodeIds);
};

// Calculate edge count for a group of nodes
export const getEdgeCountForNodes = (nodeIds: string[]): number => {
  const cy = $cytoscapeInstance.get();
  if (!cy || nodeIds.length === 0) return 0;

  // Get all edges that connect any of the nodes in the group
  // eslint-disable-next-line cypress/no-assigning-return-values
  const edges = cy.edges().filter((edge) => {
    const sourceId = edge.source().id();
    const targetId = edge.target().id();
    return nodeIds.includes(sourceId) && nodeIds.includes(targetId);
  });

  return edges.length;
};

export const removeNodeFromAIContext = (nodeId: string): void => {
  const currentGroups = $aiContextGroups.get();
  $aiContextGroups.set(
    currentGroups
      .map((group) => ({
        ...group,
        nodeIds: group.nodeIds.filter((id) => id !== nodeId),
      }))
      .filter((group) => group.nodeIds.length > 0), // Remove empty groups
  );
};

// Promise-based fit operation that ensures completion
const fitNodesWithRetry = (
  cytoscapeInstance: Core,
  nodesToSelect: NodeCollection,
  maxRetries = 2,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    let retryCount = 0;

    const attemptFit = (): void => {
      try {
        cytoscapeInstance.fit(nodesToSelect, FIT_PADDING);

        // Use Promise.race to handle either render event or timeout, ensuring only one resolution
        Promise.race([
          new Promise<void>((resolveRender) => {
            cytoscapeInstance.one("render", () => {
              resolveRender();
            });
          }),
          new Promise<void>((resolveTimeout) => {
            setTimeout(() => {
              resolveTimeout();
            }, FIT_FALLBACK_DELAY);
          }),
        ]).then(() => {
          resolve();
        });
      } catch (error) {
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(attemptFit, FIT_FALLBACK_DELAY * retryCount);
        } else {
          reject(error);
        }
      }
    };

    attemptFit();
  });
};

export const getNodeValue = (nodeId: string): string | undefined => {
  const cytoscapeInstance = $cytoscapeInstance.get();
  return cytoscapeInstance?.getElementById(nodeId)?.data("value");
};

export const selectNodesInGraph = async (nodeIds: string[]): Promise<void> => {
  const cytoscapeInstance = $cytoscapeInstance.get();

  if (!cytoscapeInstance || nodeIds.length === 0) {
    return;
  }

  // Clear current selection
  cytoscapeInstance.elements().unselect();

  // Select by exact node IDs (AI now sends actual NodeIDs)
  const nodesToSelect = cytoscapeInstance
    .nodes()
    .filter((node) => nodeIds.includes(node.id()));

  if (nodesToSelect.length > 0) {
    nodesToSelect.select();

    // Manually trigger highlighting update since programmatic selection might not trigger events
    setTimeout(() => {
      // Force a render to ensure highlighting is applied
      cytoscapeInstance.trigger("select");
    }, SELECTION_TRIGGER_DELAY);

    // Fit the view to the selected nodes with robust retry mechanism
    try {
      await fitNodesWithRetry(cytoscapeInstance, nodesToSelect);
    } catch (error) {
      console.warn("Failed to fit nodes after retries:", error);
    }
  }
};
